import React, { useRef } from "react";
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";

export default function VirtualTable({
  attributeData,
  attributeColumns,
  selectedRows,
  setSelectedRows,
  updateMapSelectionFromRows,
  tableViewMode,
}) {
  const parentRef = useRef(null);

  const tableData =
    tableViewMode === "all"
      ? attributeData
      : attributeData.filter((r) => selectedRows.includes(r.__rowId));

  const rowVirtualizer = useVirtualizer({
    count: tableData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 5,
  });

  return (
    <Box ref={parentRef} sx={{ height: "100%", overflow: "auto" }}>
      <Table stickyHeader size="small" sx={{ minWidth: "max-content" }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                bgcolor: "#e3f2fd",
                width: 40,
                borderRight: "1px solid #ddd",
                p: 0.5,
              }}
            >
              <Checkbox
                size="small"
                checked={
                  selectedRows.length === attributeData.length &&
                  attributeData.length > 0
                }
                indeterminate={
                  selectedRows.length > 0 &&
                  selectedRows.length < attributeData.length
                }
                onChange={(e) => {
                  const newSelected = e.target.checked
                    ? attributeData.map((r) => r.id)
                    : [];
                  setSelectedRows(newSelected);
                  updateMapSelectionFromRows(newSelected);
                }}
                sx={{ p: 0 }}
              />
            </TableCell>
            <TableCell
              sx={{
                bgcolor: "#e3f2fd",
                fontWeight: "bold",
                fontSize: 12,
                borderRight: "1px solid #ddd",
                width: 60,
              }}
            >
              #
            </TableCell>
            {attributeColumns.map((column) => (
              <TableCell
                key={column.id}
                sx={{
                  bgcolor: "#e3f2fd",
                  fontWeight: "bold",
                  fontSize: 12,
                  borderRight: "1px solid #ddd",
                  minWidth: 120,
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell
              colSpan={attributeColumns.length + 2}
              sx={{
                p: 0,
                border: 0,
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: "relative",
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = tableData[virtualRow.index];
                return (
                  <Table
                    key={row.__rowId}
                    size="small"
                    sx={{
                      minWidth: "max-content",
                      width: "100%",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <TableBody>
                      <TableRow
                        sx={{
                          height: 35,
                          "&:nth-of-type(odd)": {
                            bgcolor: selectedRows.includes(row.__rowId)
                              ? "#bbdefb !important"
                              : "#fafafa",
                          },
                          "&:hover": { bgcolor: "#e3f2fd" },
                          bgcolor: selectedRows.includes(row.__rowId)
                            ? "#bbdefb !important"
                            : undefined,
                          cursor: "pointer",
                          display: "table-row",
                        }}
                      >
                        <TableCell
                          sx={{
                            width: 40,
                            borderRight: "1px solid #ddd",
                            p: 0.5,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedRows.includes(row.__rowId)}
                            onChange={() => {
                              setSelectedRows((prev) => {
                                const newSelected = prev.includes(row.__rowId)
                                  ? prev.filter((id) => id !== row.__rowId)
                                  : [...prev, row.__rowId];
                                updateMapSelectionFromRows(newSelected);
                                return newSelected;
                              });
                            }}
                            sx={{ p: 0 }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 60,
                            fontSize: 11,
                            borderRight: "1px solid #ddd",
                            fontWeight: 500,
                          }}
                        >
                          {virtualRow.index + 1}
                        </TableCell>
                        {attributeColumns.map((column) => (
                          <TableCell
                            key={column.id}
                            sx={{
                              minWidth: 120,
                              fontSize: 11,
                              borderRight: "1px solid #ddd",
                            }}
                          >
                            {row[column.id] !== null &&
                            row[column.id] !== undefined
                              ? String(row[column.id])
                              : "-"}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                );
              })}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
